import React, { useEffect, useState } from "react";
import { api, fmtDate, formatError } from "@/lib/api";
import { SectionTitle, Btn, Modal, TextArea, EmptyState, reqStatusBadge } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Eye, Check, X as XIcon, Truck, Ban, Printer, Undo2, Trash2, MessageCircle, Link as LinkIcon, Pencil, Search, ChevronLeft, ChevronRight } from "lucide-react";

// Setor de Compras (Sede)
const COMPRAS_PHONE = "5593991711966";
const COMPRAS_NOME = "Nenina";

function buildComprasMessage(req, link) {
  const itens = (req.itens || []).slice(0, 8).map((it, i) => `${i + 1}. ${it.codigo} · ${it.nome} — ${it.quantidade_solicitada} ${it.unidade || "UN"}`).join("\n");
  const extra = (req.itens || []).length > 8 ? `\n...(+${req.itens.length - 8} itens — ver PDF no link)` : "";
  return (
    "COOMFLONA - Requisição de Material\n\n" +
    `Olá ${COMPRAS_NOME}, segue requisição para análise.\n\n` +
    `Nº: ${req.numero_sequencial || req.numero}\n` +
    `Solicitante: ${req.requester_nome}\n` +
    (req.sector_nome ? `Setor: ${req.sector_nome}\n` : "") +
    (req.motivo ? `Motivo: ${req.motivo}\n` : "") +
    `\nItens:\n${itens}${extra}\n\n` +
    "Aprovar / Rejeitar / PDF:\n" +
    link
  );
}

const UNIDADES_MEDIDA = ["UN", "PC", "PCT", "CX", "Fardo", "Saco", "Litro", "Kg", "Metro", "Rolo", "Par", "Cuba"];
const CATEGORIAS_REQ = [
  { value: "SEDE", label: "SEDE" },
  { value: "MATERIAL", label: "MATERIAL" },
  { value: "ALIMENTACAO", label: "ALIMENTAÇÃO" },
  { value: "OUTROS", label: "OUTROS" },
];
const emptyComprasItem = { modo: "livre", product_id: "", nome: "", quantidade: 1, unidade: "UN", preco_unitario: 0, search: "" };

// Componente de busca de produto com autocomplete
function ProductAutocomplete({ products, row, i, cSetRow, testid }) {
  const [focused, setFocused] = React.useState(false);
  const q = (row.search || row.nome || "").toLowerCase().trim();
  const matches = q.length >= 1
    ? products.filter((p) => {
        const hay = `${p.codigo} ${p.nome} ${p.categoria || ""}`.toLowerCase();
        return hay.includes(q);
      }).slice(0, 10)
    : [];
  const showList = focused && matches.length > 0 && !row.product_id;
  return (
    <div className="relative flex-1 min-w-0">
      <input
        type="text"
        value={row.product_id ? `${products.find((p) => p.id === row.product_id)?.codigo || ""} · ${products.find((p) => p.id === row.product_id)?.nome || ""}` : (row.search || "")}
        onChange={(e) => cSetRow(i, "search", e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder="Digite código/nome do produto..."
        className="w-full border-2 border-[#111] p-2 text-sm bg-white"
        data-testid={testid}
      />
      {row.product_id && (
        <button
          type="button"
          onClick={() => cSetRow(i, "product_id", "")}
          className="absolute right-1 top-1/2 -translate-y-1/2 text-[#E60000] text-xs font-mono-data hover:underline px-1"
          data-testid={`${testid}-clear`}
        >
          limpar
        </button>
      )}
      {showList && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 border-2 border-[#111] bg-white shadow-lg max-h-56 overflow-y-auto" data-testid={`${testid}-list`}>
          {matches.map((p) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); cSetRow(i, "product_id", p.id); }}
              className="block w-full text-left px-2 py-1.5 text-sm hover:bg-[#FF4F00] hover:text-white border-b border-[#EEE] last:border-b-0"
              data-testid={`${testid}-item-${p.id}`}
            >
              <span className="font-mono-data font-bold text-xs">{p.codigo}</span>
              <span className="ml-2">{p.nome}</span>
              <span className="ml-2 text-[10px] text-[#777] uppercase">{p.estoque_atual} disp · R$ {Number(p.preco_custo || 0).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Requisitions() {
  const { user, hasRole } = useAuth();
  const isCompras = user?.role === "compras";
  const canApprove = hasRole("almoxarife");
  const canEditReq = canApprove || hasRole("admin") || hasRole("compras");
  const canDelete = hasRole("admin", "almoxarife");
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [openCompras, setOpenCompras] = useState(false);
  const [detail, setDetail] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [categoriaGeral, setCategoriaGeral] = useState("MATERIAL");
  const [itens, setItens] = useState([]);
  // Formulário do NOVO item (não é a lista de itens da req)
  const [newItem, setNewItem] = useState({ modo: "catalogo", product_id: "", nome: "", quantidade: 1, unidade: "UN", search: "" });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  // Estado do modal EXCLUSIVO do Setor de Compras (template oficial Elias / Sede)
  const [comprasForm, setComprasForm] = useState({
    categoria: "SEDE",
    para: "",
    endereco: "",
    observacoes: "",
    itens: [{ ...emptyComprasItem }],
  });
  // Filtros da lista de requisições (busca/categoria/status) + paginação
  const [search, setSearch] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const load = async () => {
    setLoading(true);
    const [r, p] = await Promise.all([api.get("/requisitions"), api.get("/products")]);
    setItems(r.data); setProducts(p.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // ===== Modal Genérico (Admin/Almoxarife · KM117/Cuparí) =====
  // Fluxo: preenche o NEW ITEM (produto + qtd + unidade) → clica "+ Adicionar item" → vira linha em `itens`.
  // Só depois clica "Enviar Requisição" (valida `itens.length > 0`).
  const setNewItemField = (k, v) => {
    setNewItem((f) => {
      const nf = { ...f, [k]: v };
      if (k === "product_id") {
        if (v) {
          const prod = products.find((p) => p.id === v);
          if (prod) {
            if (!f._unidadeTouched) nf.unidade = prod.unidade || "UN";
            nf.search = "";
          }
        } else {
          nf.search = "";
        }
      }
      if (k === "modo" && v === "catalogo") { nf.nome = ""; nf.product_id = ""; nf.search = ""; }
      if (k === "modo" && v === "livre") { nf.product_id = ""; nf.search = ""; }
      if (k === "unidade") nf._unidadeTouched = true;
      return nf;
    });
  };
  // Compat com ProductAutocomplete que espera (i, k, v) — aqui só um item, i é ignorado
  const setNewItemViaAutocomplete = (_i, k, v) => setNewItemField(k, v);

  const addRow = () => {
    const qtd = parseFloat(newItem.quantidade);
    if (!qtd || qtd <= 0) { toast.error("Informe uma quantidade maior que zero"); return; }
    if (newItem.modo === "catalogo") {
      let pid = newItem.product_id;
      // TOLERÂNCIA: se o usuário digitou algo mas não clicou na sugestão,
      // tenta encontrar UM match único ou match exato.
      if (!pid && (newItem.search || "").trim()) {
        const q = newItem.search.trim().toLowerCase();
        const exact = products.find((p) => (p.codigo || "").toLowerCase() === q || (p.nome || "").toLowerCase() === q);
        if (exact) {
          pid = exact.id;
        } else {
          const matches = products.filter((p) => `${p.codigo} ${p.nome}`.toLowerCase().includes(q));
          if (matches.length === 1) {
            pid = matches[0].id;
          } else if (matches.length === 0) {
            toast.error(`Nenhum produto encontrado para "${newItem.search}". Verifique se está cadastrado ou use o modo "Livre".`);
            return;
          } else {
            toast.error(`Encontrei ${matches.length} produtos. Clique numa sugestão para escolher.`);
            return;
          }
        }
      }
      if (!pid) {
        toast.error("Digite o nome ou código do produto e clique na sugestão");
        return;
      }
      const prod = products.find((p) => p.id === pid);
      if (!prod) { toast.error("Produto não encontrado"); return; }
      setItens((cur) => {
        const idx = cur.findIndex((x) => x.product_id === pid && (x.unidade || "UN") === (newItem.unidade || "UN"));
        if (idx >= 0) {
          const copy = [...cur];
          copy[idx] = { ...copy[idx], quantidade: parseFloat(copy[idx].quantidade) + qtd };
          return copy;
        }
        return [...cur, {
          product_id: pid,
          codigo: prod.codigo, nome: prod.nome,
          quantidade: qtd,
          unidade: newItem.unidade || prod.unidade || "UN",
        }];
      });
    } else {
      // Livre — texto manual, sem produto do catálogo
      const nome = (newItem.nome || "").trim();
      if (!nome) { toast.error("Digite a descrição do item (modo livre)"); return; }
      setItens((cur) => [...cur, {
        product_id: "",
        codigo: "",
        nome,
        quantidade: qtd,
        unidade: newItem.unidade || "UN",
      }]);
    }
    // Limpa o form mantendo o modo escolhido
    setNewItem((f) => ({ modo: f.modo, product_id: "", nome: "", quantidade: 1, unidade: "UN", search: "" }));
  };

  const removeRow = (i) => setItens((cur) => cur.filter((_, idx) => idx !== i));

  // ===== Setor de Compras — modal exclusivo (Elias / Sede) =====
  const resetCompras = () => setComprasForm({
    categoria: "SEDE", para: "", endereco: "", observacoes: "",
    itens: [{ ...emptyComprasItem }],
  });
  const cAddRow = () => setComprasForm((f) => ({ ...f, itens: [...f.itens, { ...emptyComprasItem }] }));
  const cRemoveRow = (i) => setComprasForm((f) => ({ ...f, itens: f.itens.filter((_, idx) => idx !== i) }));
  const cSetRow = (i, k, v) => setComprasForm((f) => {
    const copy = [...f.itens];
    copy[i] = { ...copy[i], [k]: v };
    if (k === "product_id") {
      if (v) {
        const prod = products.find((p) => p.id === v);
        if (prod) {
          copy[i].nome = prod.nome;
          copy[i].unidade = prod.unidade || "UN";
          copy[i].search = "";
          if (!copy[i].preco_unitario || parseFloat(copy[i].preco_unitario) === 0) {
            copy[i].preco_unitario = prod.preco_custo || 0;
          }
        }
      } else {
        // limpar seleção
        copy[i].nome = "";
        copy[i].search = "";
      }
    }
    if (k === "modo" && v === "catalogo") {
      copy[i].nome = ""; copy[i].product_id = ""; copy[i].search = "";
    }
    if (k === "modo" && v === "livre") {
      copy[i].product_id = ""; copy[i].search = "";
    }
    return { ...f, itens: copy };
  });
  const cItemValor = (row) => (parseFloat(row.quantidade) || 0) * (parseFloat(row.preco_unitario) || 0);
  const cTotal = comprasForm.itens.reduce((acc, r) => acc + cItemValor(r), 0);

  const submitCompras = async (e) => {
    e.preventDefault();
    const cleanItens = comprasForm.itens
      .filter((r) => parseFloat(r.quantidade) > 0)
      .map((r) => {
        const base = {
          quantidade: parseFloat(r.quantidade),
          unidade: (r.unidade || "UN").trim(),
          preco_unitario: parseFloat(r.preco_unitario) || 0,
        };
        if (r.modo === "catalogo" && r.product_id) {
          return { ...base, product_id: r.product_id };
        }
        return { ...base, product_id: "", nome: (r.nome || "").trim() };
      })
      .filter((r) => r.product_id || r.nome);
    if (cleanItens.length === 0) { toast.error("Adicione ao menos um item com descrição/produto e quantidade"); return; }
    try {
      await api.post("/requisitions", {
        motivo: comprasForm.observacoes || "",
        categoria: comprasForm.categoria,
        para: comprasForm.para,
        endereco: comprasForm.endereco,
        observacoes: comprasForm.observacoes,
        itens: cleanItens,
      });
      toast.success("Requisição criada");
      setOpenCompras(false); resetCompras(); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      // NOVO fluxo: `itens` já é a lista pronta de itens ADICIONADOS explicitamente via
      // botão "+ Adicionar item". Validação é sobre a lista, não sobre um form solto.
      const cleanItens = itens
        .filter((x) => parseFloat(x.quantidade) > 0 && (x.product_id || (x.nome || "").trim()))
        .map((x) => x.product_id
          ? { product_id: x.product_id, quantidade: parseFloat(x.quantidade), unidade: x.unidade || "UN" }
          : { product_id: "", nome: x.nome, quantidade: parseFloat(x.quantidade), unidade: x.unidade || "UN" });
      if (cleanItens.length === 0) {
        toast.error("Adicione ao menos um item à lista antes de enviar");
        return;
      }
      if (editingId) {
        await api.put(`/requisitions/${editingId}`, { motivo, categoria: categoriaGeral, itens: cleanItens });
        toast.success("Requisição atualizada");
      } else {
        await api.post("/requisitions", { motivo, categoria: categoriaGeral, itens: cleanItens });
        toast.success("Requisição enviada");
      }
      setOpen(false); setMotivo(""); setCategoriaGeral("MATERIAL"); setItens([]); setNewItem({ modo: "catalogo", product_id: "", nome: "", quantidade: 1, unidade: "UN", search: "" }); setEditingId(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setMotivo(r.motivo || "");
    setCategoriaGeral((r.categoria || "MATERIAL").toUpperCase());
    setItens((r.itens || []).map((it) => ({
      product_id: it.product_id,
      codigo: it.codigo || "",
      nome: it.nome || "",
      quantidade: it.quantidade_solicitada,
      unidade: it.unidade || "UN",
    })));
    setNewItem({ modo: "catalogo", product_id: "", nome: "", quantidade: 1, unidade: "UN", search: "" });
    setOpen(true);
  };

  const decide = async (id, action, observ = "") => {
    try {
      if (action === "deliver") {
        await api.post(`/requisitions/${id}/deliver`);
      } else if (action === "return") {
        await api.post(`/requisitions/${id}/return`, { observacoes: observ });
      } else {
        await api.post(`/requisitions/${id}/${action}`, { observacoes_aprovador: observ });
      }
      toast.success("Atualizado");
      const { data } = await api.get("/requisitions");
      setItems(data);
      if (detail) setDetail(data.find((r) => r.id === detail.id) || null);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const doReturn = (id) => {
    const obs = prompt("Motivo / condição da devolução (opcional):") ?? "";
    if (!confirm("Confirmar devolução? O estoque será restaurado.")) return;
    decide(id, "return", obs);
  };

  const doDelete = async (req) => {
    const msg = req.status === "entregue"
      ? `Excluir Requisição ${req.numero}?\n\nATENÇÃO: ela já foi entregue — o estoque dos itens entregues será restaurado.`
      : `Excluir permanentemente a Requisição ${req.numero}?`;
    if (!confirm(msg)) return;
    try {
      await api.delete(`/requisitions/${req.id}`);
      toast.success("Requisição excluída");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const sendToCompras = async (req) => {
    try {
      const { data } = await api.post(`/requisitions/${req.id}/generate-link`);
      const link = data.approval_link;
      // Atualiza item local com token gerado
      setItems((cur) => cur.map((r) => r.id === req.id ? { ...r, approval_token: data.approval_token } : r));
      const enrichedReq = { ...req, ...data };
      const msg = buildComprasMessage(enrichedReq, link);
      const enc = encodeURIComponent(msg);
      const waUrl = `https://wa.me/${COMPRAS_PHONE}?text=${enc}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
      toast.success("WhatsApp aberto · link da requisição copiado para a mensagem");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const copyApprovalLink = async (req) => {
    try {
      const { data } = await api.post(`/requisitions/${req.id}/generate-link`);
      setItems((cur) => cur.map((r) => r.id === req.id ? { ...r, approval_token: data.approval_token } : r));
      await navigator.clipboard.writeText(data.approval_link);
      toast.success("Link de aprovação copiado");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="reqs-page">
      <SectionTitle action={<Btn onClick={() => {
        if (isCompras) { resetCompras(); setOpenCompras(true); }
        else {
          setEditingId(null); setMotivo(""); setCategoriaGeral("MATERIAL");
          setItens([]); setNewItem({ modo: "catalogo", product_id: "", nome: "", quantidade: 1, unidade: "UN", search: "" });
          setOpen(true);
        }
      }} data-testid="add-req-btn"><Plus className="w-4 h-4" />Nova Requisição</Btn>}>
        Requisições
      </SectionTitle>

      {/* Filtros: busca + categoria + status */}
      <div className="brutal-card-sm p-3 mb-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#777]" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar por Nº, solicitante, motivo, para..."
            className="w-full border-2 border-[#111] pl-8 pr-2 py-2 text-sm bg-white font-mono-data"
            data-testid="req-search"
          />
        </div>
        <select
          value={filterCategoria}
          onChange={(e) => { setFilterCategoria(e.target.value); setPage(1); }}
          className="border-2 border-[#111] p-2 text-sm bg-white font-mono-data"
          data-testid="req-filter-categoria"
        >
          <option value="">Todas categorias</option>
          {CATEGORIAS_REQ.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="border-2 border-[#111] p-2 text-sm bg-white font-mono-data"
          data-testid="req-filter-status"
        >
          <option value="">Todos status</option>
          <option value="pendente">Pendente</option>
          <option value="aprovada">Aprovada</option>
          <option value="rejeitada">Rejeitada</option>
          <option value="entregue">Entregue</option>
          <option value="devolvida">Devolvida</option>
          <option value="cancelada">Cancelada</option>
        </select>
        <div className="text-xs font-mono-data text-[#777] uppercase tracking-widest" data-testid="req-count">
          {(() => {
            const q = (search || "").toLowerCase();
            const total = items.filter((r) => {
              if (filterCategoria && (r.categoria || "").toUpperCase() !== filterCategoria) return false;
              if (filterStatus && r.status !== filterStatus) return false;
              if (!q) return true;
              const hay = `${r.numero || ""} ${r.numero_sequencial || ""} ${r.requester_nome || ""} ${r.motivo || ""} ${r.para || ""} ${r.sector_nome || ""} ${(r.itens || []).map((i) => i.nome).join(" ")}`.toLowerCase();
              return hay.includes(q);
            }).length;
            return `${total} resultado(s)`;
          })()}
        </div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th>Nº</th><th>Solicitante</th><th>Setor</th><th>Itens</th><th>Status</th><th>Data</th><th className="w-44">Ações</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && items.length === 0 && <EmptyState message="Nenhuma requisição." cols={7} />}
            {(() => {
              const q = (search || "").toLowerCase();
              const filtered = items.filter((r) => {
                if (filterCategoria && (r.categoria || "").toUpperCase() !== filterCategoria) return false;
                if (filterStatus && r.status !== filterStatus) return false;
                if (!q) return true;
                const hay = `${r.numero || ""} ${r.numero_sequencial || ""} ${r.requester_nome || ""} ${r.motivo || ""} ${r.para || ""} ${r.sector_nome || ""} ${(r.itens || []).map((i) => i.nome).join(" ")}`.toLowerCase();
                return hay.includes(q);
              });
              const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
              const currentPage = Math.min(page, totalPages);
              const start = (currentPage - 1) * PAGE_SIZE;
              const pageRows = filtered.slice(start, start + PAGE_SIZE);
              if (!loading && filtered.length === 0 && items.length > 0) {
                return <tr><td colSpan={7} className="text-center py-8 text-[#777] font-mono-data text-xs uppercase tracking-widest">Nenhum resultado com esses filtros.</td></tr>;
              }
              return pageRows.map((r) => (
              <tr key={r.id} data-testid={`req-row-${r.id}`}>
                <td className="font-mono-data font-bold" data-testid={`req-numero-${r.id}`}>{r.numero_sequencial || r.numero}</td>
                <td>{r.requester_nome}</td>
                <td className="text-sm text-[#555]">{r.sector_nome || "—"}</td>
                <td className="font-mono-data text-xs">{r.itens.length} item(ns)</td>
                <td>{reqStatusBadge(r.status)}</td>
                <td className="font-mono-data text-xs">{fmtDate(r.created_at)}</td>
                <td><div className="flex gap-1 flex-wrap">
                  <button onClick={() => setDetail(r)} className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111]" title="Detalhes"><Eye className="w-3.5 h-3.5" /></button>
                  <a href={`/imprimir/requisicao/${r.id}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111] inline-flex" title="Imprimir"><Printer className="w-3.5 h-3.5" /></a>
                  {canEditReq && r.status === "pendente" && (
                    <>
                      <button onClick={() => startEdit(r)} data-testid={`edit-req-${r.id}`} className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111]" title="Editar requisição"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => sendToCompras(r)} data-testid={`send-compras-${r.id}`} className="p-1.5 hover:bg-[#25D366] hover:text-white border border-[#111]" title={`Enviar para ${COMPRAS_NOME} (Compras) via WhatsApp`}><MessageCircle className="w-3.5 h-3.5" /></button>
                      <button onClick={() => copyApprovalLink(r)} data-testid={`copy-link-${r.id}`} className="p-1.5 hover:bg-[#0A66C2] hover:text-white border border-[#111]" title="Copiar link público de aprovação"><LinkIcon className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                  {canApprove && r.status === "pendente" && (
                    <>
                      <button onClick={() => decide(r.id, "approve")} className="p-1.5 hover:bg-[#1A7F37] hover:text-white border border-[#111]" title="Aprovar internamente"><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => { const o = prompt("Motivo da rejeição:") || ""; decide(r.id, "reject", o); }} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]" title="Rejeitar"><XIcon className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                  {canApprove && r.status === "aprovada" && (
                    <button onClick={() => decide(r.id, "deliver")} className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111]" title="Entregar"><Truck className="w-3.5 h-3.5" /></button>
                  )}
                  {canApprove && r.status === "entregue" && (
                    <button onClick={() => doReturn(r.id)} className="p-1.5 hover:bg-[#1A7F37] hover:text-white border border-[#111]" title="Devolver ao estoque" data-testid={`return-req-${r.id}`}><Undo2 className="w-3.5 h-3.5" /></button>
                  )}
                  {(r.status === "pendente" || r.status === "aprovada") && (r.requester_id === user?.id || canApprove) && (
                    <button onClick={() => decide(r.id, "cancel")} className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111]" title="Cancelar"><Ban className="w-3.5 h-3.5" /></button>
                  )}
                  {canDelete && (
                    <button onClick={() => doDelete(r)} data-testid={`delete-req-${r.id}`} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]" title="Excluir requisição"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div></td>
              </tr>
              ));
            })()}
          </tbody>
        </table>
        {/* Paginação */}
        {(() => {
          const q = (search || "").toLowerCase();
          const filtered = items.filter((r) => {
            if (filterCategoria && (r.categoria || "").toUpperCase() !== filterCategoria) return false;
            if (filterStatus && r.status !== filterStatus) return false;
            if (!q) return true;
            const hay = `${r.numero || ""} ${r.numero_sequencial || ""} ${r.requester_nome || ""} ${r.motivo || ""} ${r.para || ""} ${r.sector_nome || ""} ${(r.itens || []).map((i) => i.nome).join(" ")}`.toLowerCase();
            return hay.includes(q);
          });
          const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
          if (totalPages <= 1) return null;
          const currentPage = Math.min(page, totalPages);
          return (
            <div className="flex items-center justify-between px-3 py-2 border-t-2 border-[#111] bg-[#FAFAFA]" data-testid="req-pagination">
              <span className="text-xs font-mono-data uppercase tracking-widest text-[#555]">
                Página {currentPage} de {totalPages} · {filtered.length} req(s)
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 border-2 border-[#111] disabled:opacity-30 hover:bg-[#FF4F00] hover:text-white"
                  data-testid="req-page-prev"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 border-2 border-[#111] disabled:opacity-30 hover:bg-[#FF4F00] hover:text-white"
                  data-testid="req-page-next"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      <Modal open={open} onClose={() => { setOpen(false); setEditingId(null); }} title={editingId ? "Editar Requisição" : "Nova Requisição"} size="xl">
        <form onSubmit={submit} className="space-y-4" data-testid="req-form">
          {editingId && (
            <div className="brutal-card-sm p-3 bg-[#FFF8E1] border-2 border-[#FFC107] text-xs">
              Somente requisições <strong>pendentes</strong> podem ser editadas. Ao salvar, os itens e o motivo serão substituídos.
            </div>
          )}
          <TextArea label="Motivo / Justificativa" value={motivo} onChange={setMotivo} />

          {/* Categoria */}
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-2">Categoria</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIAS_REQ.map((c) => (
                <label key={c.value} className={`flex items-center gap-2 border-2 px-3 py-1.5 cursor-pointer text-xs font-mono-data ${categoriaGeral === c.value ? "bg-[#3D6B57] text-white border-[#3D6B57]" : "border-[#111] bg-white"}`}>
                  <input
                    type="radio"
                    name="req-categoria-geral"
                    value={c.value}
                    checked={categoriaGeral === c.value}
                    onChange={(e) => setCategoriaGeral(e.target.value)}
                    data-testid={`req-cat-geral-${c.value}`}
                    className="accent-[#FF4F00]"
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          {/* Formulário do NOVO item: preenche → botão "+ ADICIONAR ITEM" empilha na lista abaixo */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Adicionar item</label>
              <span className="text-[10px] font-mono-data text-[#777] uppercase tracking-widest" data-testid="req-products-count">
                Catálogo: {products.length} produto(s) nesta unidade
              </span>
            </div>
            {products.length === 0 && newItem.modo === "catalogo" && (
              <div className="mb-2 p-2 border-2 border-[#FFC107] bg-[#FFF8E1] text-xs text-[#5A3E00] font-mono-data" data-testid="req-no-catalog-warn">
                ⚠ Não há produtos cadastrados nesta unidade. Use o modo <strong>Livre</strong> ou cadastre produtos em <strong>Estoque</strong> antes.
              </div>
            )}
            <div className="flex flex-wrap gap-2 items-end">
              {/* Toggle Catálogo/Livre */}
              <div className="inline-flex border-2 border-[#111] shrink-0 self-stretch">
                <button type="button"
                  onClick={() => setNewItemField("modo", "catalogo")}
                  data-testid="req-new-mode-catalog"
                  className={`px-2 py-2 text-[10px] font-mono-data uppercase tracking-widest ${newItem.modo === "catalogo" ? "bg-[#FF4F00] text-white" : "bg-white text-[#111]"}`}>
                  Catálogo
                </button>
                <button type="button"
                  onClick={() => setNewItemField("modo", "livre")}
                  data-testid="req-new-mode-free"
                  className={`px-2 py-2 text-[10px] font-mono-data uppercase tracking-widest border-l-2 border-[#111] ${newItem.modo === "livre" ? "bg-[#FF4F00] text-white" : "bg-white text-[#111]"}`}>
                  Livre
                </button>
              </div>
              {/* Produto (catálogo) OU Descrição (livre) */}
              {newItem.modo === "catalogo" ? (
                <ProductAutocomplete products={products} row={newItem} i={0} cSetRow={setNewItemViaAutocomplete} testid="req-item-prod-0" />
              ) : (
                <input
                  type="text"
                  value={newItem.nome}
                  onChange={(e) => setNewItemField("nome", e.target.value)}
                  placeholder="Descrição livre do item/serviço"
                  className="flex-1 min-w-0 border-2 border-[#111] p-2 text-sm bg-white"
                  data-testid="req-new-desc"
                />
              )}
              <div className="flex flex-col">
                <span className="text-[9px] uppercase font-mono-data tracking-widest text-[#777]">Qtd</span>
                <input type="number" step="0.01" min="0.01" value={newItem.quantidade}
                  onChange={(e) => setNewItemField("quantidade", e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRow(); } }}
                  className="w-20 border-2 border-[#111] p-2 text-sm"
                  placeholder="Qtd" data-testid="req-new-qtd" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] uppercase font-mono-data tracking-widest text-[#777]">Unid</span>
                <select value={newItem.unidade}
                  onChange={(e) => setNewItemField("unidade", e.target.value)}
                  className="w-28 border-2 border-[#111] p-2 text-sm bg-white font-mono-data"
                  data-testid="req-new-unidade" title="Unidade de medida">
                  {UNIDADES_MEDIDA.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <button type="button" onClick={addRow}
                className="px-3 py-2 border-2 border-[#FF4F00] bg-[#FF4F00] text-white text-xs font-mono-data uppercase tracking-widest hover:opacity-80 shrink-0 inline-flex items-center gap-1"
                data-testid="req-add-row-btn">
                <Plus className="w-3 h-3" /> Adicionar Item
              </button>
            </div>
            <p className="mt-1 text-[10px] font-mono-data text-[#777] uppercase tracking-widest">
              {newItem.modo === "catalogo"
                ? "Dica: digite o nome do produto e clique na sugestão que aparece. Se só houver 1 match, clicar em Adicionar já usa ele."
                : "Modo livre: descreva o item/serviço que não está no catálogo (ex: mão de obra, produto avulso)."}
            </p>
          </div>

          {/* Lista de itens ADICIONADOS (o que será enviado) */}
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-2">
              Itens da requisição <span className="text-[#FF4F00]">*</span>
              {itens.length > 0 && <span className="ml-2 text-[#3D6B57]">({itens.length})</span>}
            </label>
            {itens.length === 0 ? (
              <div className="border-2 border-dashed border-[#CCC] p-4 text-center text-xs font-mono-data text-[#777] uppercase tracking-widest" data-testid="req-empty-list">
                Nenhum item adicionado ainda. Use o formulário acima e clique em Adicionar Item.
              </div>
            ) : (
              <div className="border-2 border-[#111] divide-y-2 divide-[#111]">
                {itens.map((row, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-white" data-testid={`req-item-row-${i}`}>
                    <span className="font-mono-data font-bold text-xs text-[#3D6B57] w-6">{i + 1}.</span>
                    <span className="font-mono-data text-xs text-[#777] w-24 shrink-0" data-testid={`req-item-codigo-${i}`}>{row.codigo}</span>
                    <span className="flex-1 text-sm min-w-0 truncate" data-testid={`req-item-nome-${i}`}>{row.nome}</span>
                    <span className="font-mono-data text-sm font-bold shrink-0" data-testid={`req-item-qtd-${i}`}>{row.quantidade} {row.unidade}</span>
                    <button type="button" onClick={() => removeRow(i)}
                      className="p-1.5 border-2 border-[#111] hover:bg-[#E60000] hover:text-white"
                      title="Remover item"
                      data-testid={`req-item-remove-${i}`}>
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => { setOpen(false); setEditingId(null); }}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-req-btn">{editingId ? "Salvar Alterações" : "Enviar Requisição"}</Btn>
          </div>
        </form>
      </Modal>

      {/* Modal EXCLUSIVO do Setor de Compras (Elias / Sede) — template oficial */}
      <Modal open={openCompras} onClose={() => setOpenCompras(false)} title="Nova Requisição · Setor de Compras" size="xl">
        <form onSubmit={submitCompras} className="space-y-4" data-testid="req-compras-form">
          {/* Categoria */}
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-2">Categoria *</label>
            <div className="flex flex-wrap gap-4">
              {CATEGORIAS_REQ.map((c) => (
                <label key={c.value} className={`flex items-center gap-2 border-2 px-3 py-2 cursor-pointer text-sm font-mono-data ${comprasForm.categoria === c.value ? "bg-[#3D6B57] text-white border-[#3D6B57]" : "border-[#111] bg-white"}`}>
                  <input
                    type="radio"
                    name="req-categoria"
                    value={c.value}
                    checked={comprasForm.categoria === c.value}
                    onChange={(e) => setComprasForm((f) => ({ ...f, categoria: e.target.value }))}
                    data-testid={`req-cat-${c.value}`}
                    className="accent-[#FF4F00]"
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          {/* Para / Endereço */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Para (Fornecedor / Destinatário)</label>
              <input
                type="text"
                value={comprasForm.para}
                onChange={(e) => setComprasForm((f) => ({ ...f, para: e.target.value }))}
                data-testid="req-para"
                placeholder="Ex: Auto Peças Central Ltda"
                className="w-full border-2 border-[#111] p-2 text-sm bg-white"
              />
            </div>
            <div>
              <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Endereço</label>
              <input
                type="text"
                value={comprasForm.endereco}
                onChange={(e) => setComprasForm((f) => ({ ...f, endereco: e.target.value }))}
                data-testid="req-endereco"
                placeholder="Ex: Av. Cuiabá, 1234 · Santarém"
                className="w-full border-2 border-[#111] p-2 text-sm bg-white"
              />
            </div>
          </div>

          {/* Observação */}
          <TextArea label="Observação" value={comprasForm.observacoes} onChange={(v) => setComprasForm((f) => ({ ...f, observacoes: v }))} testid="req-obs" />

          {/* Itens */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Itens *</label>
              <div className="text-[10px] font-mono-data text-[#777] uppercase tracking-widest">
                Toggle Catálogo/Livre · Valor = Qtd × P.Unit
              </div>
            </div>
            <div className="space-y-2">
              {comprasForm.itens.map((row, i) => {
                const valor = cItemValor(row);
                return (
                  <div key={i} className="border-2 border-[#111] p-2 space-y-2" data-testid={`req-compras-row-${i}`}>
                    <div className="flex flex-wrap items-stretch gap-2">
                      {/* Toggle modo */}
                      <div className="inline-flex border-2 border-[#111] shrink-0">
                        <button type="button"
                          onClick={() => cSetRow(i, "modo", "catalogo")}
                          data-testid={`req-mode-catalog-${i}`}
                          className={`px-2 py-1 text-[10px] font-mono-data uppercase tracking-widest ${row.modo === "catalogo" ? "bg-[#FF4F00] text-white" : "bg-white text-[#111]"}`}>
                          Catálogo
                        </button>
                        <button type="button"
                          onClick={() => cSetRow(i, "modo", "livre")}
                          data-testid={`req-mode-free-${i}`}
                          className={`px-2 py-1 text-[10px] font-mono-data uppercase tracking-widest border-l-2 border-[#111] ${row.modo === "livre" ? "bg-[#FF4F00] text-white" : "bg-white text-[#111]"}`}>
                          Livre
                        </button>
                      </div>
                      {/* Descrição / Produto */}
                      {row.modo === "catalogo" ? (
                        <ProductAutocomplete products={products} row={row} i={i} cSetRow={cSetRow} testid={`req-prod-${i}`} />
                      ) : (
                        <input
                          type="text"
                          value={row.nome}
                          onChange={(e) => cSetRow(i, "nome", e.target.value)}
                          placeholder="Descrição livre do item/serviço"
                          className="flex-1 min-w-0 border-2 border-[#111] p-2 text-sm bg-white"
                          data-testid={`req-desc-${i}`}
                        />
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-[repeat(5,auto)_1fr_auto] items-center gap-2">
                      <div className="flex flex-col">
                        <span className="text-[9px] uppercase font-mono-data tracking-widest text-[#777]">Qtd</span>
                        <input
                          type="number" step="0.01" min="0.01"
                          value={row.quantidade}
                          onChange={(e) => cSetRow(i, "quantidade", e.target.value)}
                          className="w-20 border-2 border-[#111] p-1.5 text-sm"
                          data-testid={`req-qtd-${i}`}
                        />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[9px] uppercase font-mono-data tracking-widest text-[#777]">Unid</span>
                        <select
                          value={row.unidade}
                          onChange={(e) => cSetRow(i, "unidade", e.target.value)}
                          className="w-24 border-2 border-[#111] p-1.5 text-sm bg-white font-mono-data"
                          data-testid={`req-unid-${i}`}
                        >
                          {UNIDADES_MEDIDA.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[9px] uppercase font-mono-data tracking-widest text-[#777]">P.Unit</span>
                        <input
                          type="number" step="0.01" min="0"
                          value={row.preco_unitario}
                          onChange={(e) => cSetRow(i, "preco_unitario", e.target.value)}
                          className="w-24 border-2 border-[#111] p-1.5 text-sm"
                          data-testid={`req-punit-${i}`}
                        />
                      </div>
                      <div className="col-span-2 sm:col-span-1 sm:col-start-6 text-right font-mono-data text-sm min-w-0">
                        <span className="text-[10px] uppercase text-[#777] tracking-widest mr-2">Valor</span>
                        <span className="font-bold" data-testid={`req-valor-${i}`}>R$ {valor.toFixed(2)}</span>
                      </div>
                      {comprasForm.itens.length > 1 && (
                        <button type="button" onClick={() => cRemoveRow(i)} className="justify-self-end p-2 border-2 border-[#111] hover:bg-[#E60000] hover:text-white" data-testid={`req-remove-${i}`}>
                          <XIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <button type="button" onClick={cAddRow} data-testid="req-add-row" className="mt-2 text-xs font-mono-data uppercase tracking-widest text-[#FF4F00] hover:underline">+ Adicionar item</button>
          </div>

          {/* Total */}
          <div className="flex justify-end items-center gap-3 border-t-2 border-[#111] pt-3">
            <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Total geral</span>
            <span className="font-mono-data text-lg font-bold" data-testid="req-total">R$ {cTotal.toFixed(2)}</span>
          </div>

          <div className="flex justify-end gap-2">
            <Btn type="button" variant="secondary" onClick={() => setOpenCompras(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-req-compras-btn">Enviar Requisição</Btn>
          </div>
        </form>
      </Modal>

      {detail && (
        <Modal open={true} onClose={() => setDetail(null)} title={`${detail.numero_sequencial || detail.numero} · ${detail.requester_nome}`} size="lg">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Status</div>{reqStatusBadge(detail.status)}</div>
              <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Setor</div><div>{detail.sector_nome || "—"}</div></div>
              <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Criada em</div><div className="font-mono-data text-xs">{fmtDate(detail.created_at)}</div></div>
              <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Aprovador</div><div>{detail.aprovador_nome || "—"}</div></div>
            </div>
            {detail.motivo && <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Motivo</div><div className="border-2 border-[#111] p-2 text-sm bg-[#FAFAFA]">{detail.motivo}</div></div>}
            {detail.observacoes_aprovador && <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Observação do aprovador</div><div className="border-2 border-[#111] p-2 text-sm bg-[#FAFAFA]">{detail.observacoes_aprovador}</div></div>}
            <table className="brutal-table">
              <thead><tr><th>Código</th><th>Item</th><th className="text-right">Solicitado</th><th className="text-right">Entregue</th></tr></thead>
              <tbody>
                {detail.itens.map((it, i) => (
                  <tr key={i}>
                    <td className="font-mono-data text-xs">{it.codigo}</td>
                    <td>{it.nome}</td>
                    <td className="text-right font-mono-data">{it.quantidade_solicitada} {it.unidade}</td>
                    <td className="text-right font-mono-data font-bold">{it.quantidade_entregue || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </div>
  );
}
